;(function (angular, _, moment) {
  'use strict'

  class InvoiceService {
    $q
    $http
    $state
    exportService
    preferencesService
    invoiceManager
    snackService
    statusService
    utilService
    sessionService
    paymentService
    urls
    quoteInvoiceDueDateCompany
    $uibModal

    constructor (
      // Angular
      $http, $q, $state,
      // Libs
      $uibModal,
      // Managers
      InvoiceManager,
      // Services
      ExportService, PaymentService, PreferencesService, SnackService, StatusService, UtilService, SessionService,
      // Const
      urls, quoteInvoiceDueDateCompany
      // Resolved
    ) {
      this.$http = $http
      this.$q = $q
      this.$state = $state
      this.$uibModal = $uibModal
      this.invoiceManager = InvoiceManager
      this.exportService = ExportService
      this.paymentService = PaymentService
      this.preferencesService = PreferencesService
      this.snackService = SnackService
      this.statusService = StatusService
      this.utilService = UtilService
      this.urls = urls
      this.quoteInvoiceDueDateCompany = quoteInvoiceDueDateCompany
      this.company = SessionService.getCompany()
    }

    get manager () {
      return this.invoiceManager
    }

    createInvoiceFromPreferences () {
      return this.$q.all([
        this.utilService.getLegalNotices(),
        this.preferencesService.getInvoicePrefs()
      ]).then(([legalNotices, invoicePrefs]) => {
        return this.invoiceManager.create({
          status: 'draft',
          history: [],
          conditions: _.get(invoicePrefs, 'defaultConditions', '').replace(/\n/g, '<br>'),
          thanks: _.get(invoicePrefs, 'defaultThanks', '').replace(/\n/g, '<br>'),
          legalNotices: legalNotices,
          customFields: {}
        })
      })
    }

    /**
     * Update invoice status
     * @param invoice {Invoice}
     * @param status {'validated' | 'dunning'| 'sent' | 'partly_paid' | 'litigation'}
     */
    setStatus (invoice, status) {
      this.loading = true
      switch (status) {
        case 'validated':
          return this._validate(invoice)
        case 'sent':
          return this._sendInvoice(invoice)
        case 'partly_paid':
          return this.makePayment(invoice)
        case 'dunning':
          return this._sendDunningMail(invoice)
        case 'litigation':
          return this._sendLitigationMail(invoice)
        default:
      }
    }

    makePayment (invoice, creditNote) {
      return this.paymentService.addInvoicePayment(invoice, creditNote)
    }

    /**
     * Return available status for given invoice
     * @param invoice
     * @return {*}
     */
    getAllowedStatus (invoice) {
      return this.statusService.getAllowedStatus('invoice', invoice.status).filter(v => v !== 'paid')
    }

    /**
     * Duplicate given invoice and redirect to invoice creation page
     * @param invoice
     * @return {Promise<void>}
     */
    duplicate (invoice) {
      const i = _.cloneDeep(invoice)
      if (i.chorusFields) delete i.chorusFields
      i.invoiceDeposit = []
      i.payments = []
      if (i.numberFormat) delete i.numberFormat
      const _invoice = this.invoiceManager.create(i.duplicate())
      // La copie est une nouvelle facture : jamais émise sur Pennylane et non annulée
      _invoice.sentToPennylane = false
      _invoice.cancelByUser = false
      return this.getDueDate(invoice).then(v => {
        _invoice.dueDate = v
        this.snackService.snack({
          text: 'Facture dupliquée !',
          delay: 5000
        })
        this.$state.go('invoice.new', { initParams: _invoice })
      })
    }

    changeDueDate (invoice) {
      if (invoice.immediatePayment) {
        invoice.dueDate = null
        return this.$q.resolve(invoice)
      }
      return this.getDueDate(invoice, invoice.date).then(v => {
        invoice.dueDate = v
        return invoice
      })
    }

    /**
     * Get dueDate based on invoice client or invoice prefs
     * @param invoice {Invoice}
     * @param date
     * @return {Promise<void>}
     * @private
     */
    getDueDate (invoice, date = new Date()) {
      const _date = moment(date)
      return this.preferencesService.getPrefsFor('invoice').then((prefs) => {
        if (invoice?.client?.paymentDelay) {
          if (invoice.client.paymentDelayEndOfMonth) {
            return moment(_date).add(invoice.client.paymentDelay, 'days').endOf('month').toDate()
          } else if (invoice.client.paymentEndOfMonthDelay) {
            return moment(_date).endOf('month').add(invoice.client.paymentDelay, 'days').toDate()
          }
          return moment(_date).add(invoice.client.paymentDelay, 'days').toDate()
        }
        if (prefs.invoiceDelay !== 0) {
          if (prefs.invoiceDelayEndOfMonth) {
            return moment(_date).add(prefs.invoiceDelay, 'days').endOf('month').toDate()
          } else if (prefs.invoiceEndOfMonthDelay) {
            return moment(_date).endOf('month').add(prefs.invoiceDelay, 'days').toDate()
          }
          return moment(_date).add(prefs.invoiceDelay, 'days').toDate()
        }
        return moment(_date).toDate()
      })
    }

    asPdf (invoiceId) {
      return this.exportService.invoiceAsPdf(invoiceId)
    }
    asFacturX (invoiceId) {
      return this.exportService.invoiceAsFacturX(invoiceId)
    }

    asPdfWithInterventions (invoiceId) {
      return this.exportService.invoiceAsPdfWithInterventions(invoiceId)
    }

    asProforma (invoiceId) {
      return this.exportService.invoiceAsPdf(invoiceId, true)
    }

    asCsv (invoiceId) {
      return this.exportService.invoiceAsCsv(invoiceId)
    }

    asSpreadsheet (invoiceId) {
      return this.exportService.invoiceAsSpreadsheet(invoiceId)
    }

    searchToSpreadsheet (conditions, total) {
      return this.exportService.searchToSpreadsheet(conditions, total, 'invoice')
    }

    checkAllowedVats (products, companyVats) {
      let invoiceVats = []
      products.forEach(product => {
        invoiceVats.push(product.VAT)
      })
      let isAllowed = invoiceVats.some((e) => {
        return companyVats.includes(e)
      })
      return isAllowed
    }

    // PRIVATES
    _validate (invoice) {
      return this.preferencesService.getPrefsFor('company').then((prefs) => {
        if (!this.checkAllowedVats(invoice.lines, prefs.vats)) {
          return this.$uibModal.open({
            controllerAs: 'invalid',
            bindToController: true,
            resolve: {
              invalidType: [() => 'tva']
            },
            size: 'lg',
            templateUrl: '/app/modules/invoice/show/templates/validate-error.template.html',
            controller: 'ValidateErrorController'
          }).result.then((result) => {
            return this.$q.reject()
          })
        } else {
          return this.preferencesService.getInvoicePrefs().then((invoicePrefs) => {
            const emitDate = moment(invoice.date).format('YYYY-MM-DD')
            const emitDateStart = invoicePrefs?.emitPeriod?.start ? moment(invoicePrefs.emitPeriod.start).format('YYYY-MM-DD') : null
            const emitDateEnd = invoicePrefs?.emitPeriod?.end ? moment(invoicePrefs.emitPeriod.end).format('YYYY-MM-DD') : null
            if (!(emitDateEnd && emitDateStart) || moment(emitDate).isBetween(emitDateStart, emitDateEnd, null, '[]')) {
              return this.$uibModal.open({
                controllerAs: 'valid',
                bindToController: true,
                resolve: {
                  objType: [() => 'invoice'],
                  date: _.get(invoice, 'date', new Date())
                },
                templateUrl: '/app/modules/invoice/show/templates/validate.template.html',
                controller: 'ValidateInvoiceController'
              }).result.then((result) => {
                if (result && result.close) {
                  if (result.today) {
                    invoice.date = new Date()
                    delete invoice.dueDate
                  }
                  return this._switchToStatus(invoice, 'validated').then((i) => {
                    this.snackService.snack({
                      text: 'Facture validée!',
                      delay: 5000
                    })
                    return i
                  })
                }
              })
            }
            return this.$uibModal.open({
              controllerAs: 'invalid',
              bindToController: true,
              size: 'lg',
              templateUrl: '/app/modules/invoice/show/templates/validate-error.template.html',
              controller: 'ValidateErrorController'
            }).result.then((result) => {
              return this.$q.reject()
            })
          })
        }
      })
    }

    _sendInvoice (invoice) {
      return this.$uibModal.open({
        controllerAs: 'send',
        bindToController: true,
        resolve: {
          obj: [() => invoice],
          type: [() => 'invoice'],
          asInter: !!_.get(invoice, 'linkedData.solica.interventions', []).length
        },
        templateUrl: '/app/modules/invoice/show/templates/send-invoice.template.html',
        controller: 'SendInvoiceController'
      }).result.then((result) => {
        const promises = []
        if (result.mail) {
          const recipients = result.emails.filter(function (email) {
            return email.added
          }).map(function (e) { return { Email: e.email } })

          let url = this.urls.backend
          if (!result.withInter) {
            url += '/services/pdf/invoice/emails/'
          } else {
            url += '/services/pdf/invoice-with-interventions/emails/'
          }
          if (result.mailTemplate) {
            url += result.mailTemplate._id
          }
          const toSend = {
            invoices: [invoice._id],
            recipients: recipients,
            email: result.customMailTemplate,
            subject: result.subject
          }
          if (result.model && result.model.length > 0) {
            toSend.model = result.model
            toSend.smtp = true
          }
          if (recipients.length) {
            promises.push(this.$http.post(url, toSend))
          }
        }

        if (result.print) {
          // TODO: print
        }
        if (result.status) {
          promises.push(this._switchToStatus(invoice, 'sent'))
        }
        return this.$q.all(promises).then((results) => {
          this.snackService.snack({
            text: 'Facture envoyée !',
            delay: 5000
          })
          if (!result.status) {
            return invoice
          }
          // Get updated invoice form promises
          return results[results.length - 1]
        })
      })
    }

    _sendDunningMail (invoice) {
      return this.$uibModal.open({
        controllerAs: 'sendDunning',
        bindToController: true,
        resolve: {},
        templateUrl: '/app/modules/invoice/show/templates/send-dunning.template.html',
        controller: 'SendDunningController'
      }).result.then((result) => {
        if (result) {
          // TODO: modele de mail de relance ?
          return this._switchToStatus(invoice, 'dunning').then((i) => {
            this.snackService.snack({
              text: 'Facture passée en état "Relance" !',
              delay: 5000
            })
            return i
          })
        }
      })
    }

    _sendLitigationMail (invoice) {
      return this.$uibModal.open({
        controllerAs: 'sendLitigation',
        bindToController: true,
        resolve: {},
        templateUrl: '/app/modules/invoice/show/templates/send-litigation.template.html',
        controller: 'SendLitigationController'
      }).result.then((result) => {
        if (result) {
          return this._switchToStatus(invoice, 'litigation').then((i) => {
            this.snackService.snack({
              text: 'Facture passée en état "Litige" !',
              delay: 5000
            })
            return i
          })
        }
      })
    }

    /**
     * Update invoice status
     * @param invoice {Invoice}
     * @param status {'validated' | 'dunning'| 'sent' | 'partly_paid' | 'litigation'}
     * @return {Promise<*>|void|*}
     * @private
     */
    _switchToStatus (invoice, status) {
      if (status === 'validated') {
        return this.preferencesService.getInvoicePrefs().then((invoicePrefs) => {
          invoice.status = status
          invoice.periodicity = invoicePrefs.periodicity
          invoice.resetDate = invoicePrefs.resetDate
          invoice.numberFormat = invoicePrefs.numberFormat
          if (!invoice.date || this.quoteInvoiceDueDateCompany.filter(e => e.value === this.company).length > 0) {
            invoice.date = new Date()
          }
          if ((!invoice.dueDate && !invoice.immediatePayment) || this.quoteInvoiceDueDateCompany.filter(e => e.value === this.company).length > 0) {
            return this.changeDueDate(invoice).then((r) => {
              return r.validate()
            })
          } else {
            return invoice.validate()
          }
        })
      }
      return invoice.setStatus(status)
    }

    /**
     * Create a new invoice of instance with only field we will update
     * @param invoice {Invoice}
     * @return {Invoice}
     * @private
     */
    _getInstance (invoice) {
      return this.invoiceManager.create({ _id: invoice._id })
    }
  }

  InvoiceService.$inject = [
    // Angular
    '$http', '$q', '$state',
    // Libs
    '$uibModal',
    // Managers
    'InvoiceManager',
    // Services
    'ExportService', 'PaymentService', 'PreferencesService', 'SnackService', 'StatusService', 'UtilService', 'SessionService',
    // Const
    'urls', 'quoteInvoiceDueDateCompany'
    // Resolved
  ]
  angular
    .module('kilo.common.services')
    .service('InvoiceService', InvoiceService)
})(window.angular, window._, window.moment)
